import React, { FC, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { GridColumns, GridRowData } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { DateFormat } from '../../helpers/dateFormat';
import { getStringEqualsOperator } from '../../filterOperators/stringEqualsOperator';
import { getStringDiffersOperator } from '../../filterOperators/stringDiffersOperator';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../../filterOperators/dateFilterOperators';
import { PageHeader } from '../PageHeader/PageHeader';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DealHistoryFetcher } from '../../fetchers/DealHistoryFetcher';
import { useAutoFetch } from '../../contexts/Fetcher';

export const DealHistory: FC = () => {
  const history = useHistory();
  const { t } = useTranslation();

  interface IDealHistory {
    productId: number;
    createdAt: string;
    id: number;
    organizationUuid: string;
    dealId: string;
  }

  const { data } = useContext(DealHistoryFetcher.Context) as { data: IDealHistory[] | null };

  useAutoFetch(DealHistoryFetcher);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'dealId',
        headerName: t('common.tableHeader.dealId'),
        flex: 1,
        filterOperators: [getStringEqualsOperator(), getStringDiffersOperator()],
      },
      {
        field: 'productId',
        headerName: t('common.tableHeader.productId'),
        flex: 1,
        filterOperators: [getStringEqualsOperator(), getStringDiffersOperator()],
      },
      {
        field: 'createdAt',
        headerName: t('common.tableHeader.created'),
        width: 200,
        valueFormatter: getDateValueFormatter(DateFormat.DateFullWithTimeAndZone),
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
    ],
    [t]
  );

  const rows = useMemo<GridRowData[]>(() => {
    if (!data) return [];
    return data.map((dealHistory: any, index: number) => ({
      ...dealHistory,
      id: dealHistory.id ?? index, // Use the ID from API or fallback to index
    }));
  }, [data]);

  const handleBack = () => {
    history.goBack();
  };

  const getRowLink = (row: any) => {
    const dealId = row.row.dealId;
    if (dealId) {
      return {
        url: `https://app.hubspot.com/contacts/9267851/record/0-3/${dealId}`,
        external: true,
      };
    }
    return null;
  };

  if (!data) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <PageHeader title="Deal History">
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} variant="outlined">
          {t('common.back')}
        </Button>
      </PageHeader>

      <UrlQueryDataGrid
        columns={columns}
        rows={rows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        getRowLink={getRowLink}
      />
    </div>
  );
};
